import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DashboardTab,
  EditProjectDialog,
  FinancialsTab,
  MembersTab,
  ProgressTab,
  ProjectHeader,
  RosterTab,
} from '@/features/projects/components';
import { tabFromSearch } from '@/features/projects/lib/project-tab';
import { BoardsTab } from '@/features/projects/components/boards-tab';
import { ArchiveBoardsChoice } from '@/features/projects/components/archive-boards-choice';
import { boardsReaderMaySet } from '@/features/projects/lib/project-boards';
import { useFreshOnOpen } from '@/features/projects/hooks/use-fresh-on-open';
import { useBoards } from '@/hooks/use-boards';
import { httpStatus } from '@/lib/http-status';
import type { ArchiveBoardChoice } from '@/lib/projects';
import type { BoardListItem } from '@hygge/shared';
import { useArchiveProject, useProject, useProjectMembers, useUpdateProject } from '@/hooks/use-projects';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import type { ProjectDetail } from '@hygge/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  BarChart3,
  DollarSign,
  Kanban,
  LayoutDashboard,
  Lock,
  Sheet,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

type TabType = 'dashboard' | 'members' | 'roster' | 'progress' | 'boards' | 'financials';

interface TabDef {
  id: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  placeholder?: boolean;
  restricted?: boolean;
}

const allTabs: TabDef[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'roster', label: 'Roster', icon: Sheet },
  { id: 'progress', label: 'Progress', icon: BarChart3 },
  { id: 'boards', label: 'Boards', icon: Kanban },
  { id: 'financials', label: 'Financials', icon: DollarSign, restricted: true },
];

export const Route = createFileRoute('/projects/$id')({
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data: project, isLoading } = useProject(id);
  const { data: projectMembers } = useProjectMembers(id);
  // Opened on the tab its address names (HW-983), so Back from a board draws
  // the Boards tab at once rather than Dashboard first. Financials is not known
  // to be visible yet; the effect below admits it once the rights load.
  const [activeTab, setActiveTab] = useState<TabType>(() =>
    tabFromSearch(
      window.location.search,
      allTabs.filter((t) => !t.restricted).map((t) => t.id),
      'dashboard',
    ),
  );
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false);
  const archiveMutation = useArchiveProject();
  const updateMutation = useUpdateProject();
  const {
    data: projectBoards = [],
    isError: isBoardsError,
    error: boardsError,
    refetch: refetchBoards,
  } = useBoards({ projectId: id });
  // The archive question waits for a list fetched afresh when it opens.
  const boardsFresh = useFreshOnOpen(isArchiveOpen ? id : null, refetchBoards);
  const isLoadingBoards = isArchiveOpen && !boardsFresh;
  // Refused outright (no Open Boards) is an answer — there is nothing to ask about.
  const boardsLoadFailed = isBoardsError && httpStatus(boardsError) !== 403;
  const [archiveBoards, setArchiveBoards] = useState<ArchiveBoardChoice | null>(null);


  const canViewFinancials = useMemo(() => {
    if (!user || !project) return false;
    if (user.role === 'super_admin') return true;
    const isCurrentUserPM = projectMembers?.some(
      (m) => m.isManager && !m.removedAt && m.employeeId === user.employee?.id,
    );
    if (user.role === 'manager' && isCurrentUserPM) return true;
    return user.permissions?.includes('projects.financials.view') ?? false;
  }, [user, project, projectMembers]);

  const tabs = useMemo(
    () => allTabs.filter((t) => !t.restricted || canViewFinancials),
    [canViewFinancials],
  );

  // The tab written into `?tab=` is read back again whenever the visible tabs
  // change: Financials becomes reachable once the reader's rights load, and a
  // tab the reader may no longer see falls back to Dashboard (HW-983).
  useEffect(() => {
    const fromUrl = tabFromSearch(
      window.location.search,
      tabs.map((t) => t.id),
      'dashboard',
    );
    setActiveTab((current) => (current === fromUrl ? current : fromUrl));
  }, [tabs]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url);
  };

  const handleArchive = async () => {
    await archiveMutation.mutateAsync({ id, boards: archiveBoards ?? undefined });
    navigate({ to: '/projects' });
  };

  const handleUnarchive = async () => {
    await updateMutation.mutateAsync({ id, status: 'active' });
    setIsUnarchiveOpen(false);
  };

  if (isLoading) {
    return <ProjectDetailSkeleton />;
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-muted-foreground">Project not found</p>
        <Link to="/projects" className="text-sm text-hygge hover:underline mt-2">
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 px-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/projects" className="hover:text-foreground transition-colors">
            Projects
          </Link>
          <span>/</span>
          <span className="font-medium text-foreground">{project.name}</span>
        </div>
      </header>

      <div className="p-6 max-w-6xl">
        {/* Back button */}
        <Link
          to="/projects"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5 -ml-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </Link>

        {/* Project header */}
        <ProjectHeader
          project={project}
          onEdit={() => setIsEditOpen(true)}
          onArchive={() => setIsArchiveOpen(true)}
          onUnarchive={() => setIsUnarchiveOpen(true)}
        />

        {/* Tab navigation */}
        <div className="mt-6 border-b border-border">
          <div className="flex items-end justify-between">
            <nav className="flex gap-0" aria-label="Project tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={cn(
                      'relative flex items-center gap-1.5 px-4 pb-3 pt-1 text-sm font-medium transition-colors cursor-pointer',
                      isActive
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {tab.label}
                    {tab.restricted && (
                      <Lock className="h-3 w-3 text-muted-foreground/50" />
                    )}
                    {isActive && (
                      <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-hygge rounded-t-full" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab content */}
        <div className="mt-6">
          {activeTab === 'dashboard' && <DashboardTab project={project} />}
          {activeTab === 'members' && <MembersTab project={project} />}
          {activeTab === 'roster' && <RosterTab project={project} />}
          {activeTab === 'progress' && <ProgressTab project={project} />}
          {activeTab === 'boards' && <BoardsTab projectId={project.id} />}
          {activeTab === 'financials' && canViewFinancials && <FinancialsTab project={project} />}
        </div>
      </div>

      {/* Edit dialog */}
      <EditProjectDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        project={project}
      />

      {/* Archive confirmation */}
      <ArchiveDialog
        mode="archive"
        open={isArchiveOpen}
        onOpenChange={(open) => {
          setIsArchiveOpen(open);
          if (!open) setArchiveBoards(null);
        }}
        project={project}
        onConfirm={handleArchive}
        isPending={archiveMutation.isPending}
        boards={boardsReaderMaySet(projectBoards)}
        isLoadingBoards={isLoadingBoards}
        boardsLoadFailed={boardsLoadFailed}
        boardsChoice={archiveBoards}
        onBoardsChoiceChange={setArchiveBoards}
      />

      {/* Unarchive confirmation */}
      <ArchiveDialog
        mode="unarchive"
        open={isUnarchiveOpen}
        onOpenChange={setIsUnarchiveOpen}
        project={project}
        onConfirm={handleUnarchive}
        isPending={updateMutation.isPending}
      />
    </>
  );
}

function ArchiveDialog({
  mode,
  open,
  onOpenChange,
  project,
  onConfirm,
  isPending,
  boards = [],
  isLoadingBoards = false,
  boardsLoadFailed = false,
  boardsChoice = null,
  onBoardsChoiceChange,
}: {
  mode: 'archive' | 'unarchive';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDetail;
  onConfirm: () => void;
  isPending: boolean;
  boards?: BoardListItem[];
  isLoadingBoards?: boolean;
  boardsLoadFailed?: boolean;
  boardsChoice?: ArchiveBoardChoice | null;
  onBoardsChoiceChange?: (value: ArchiveBoardChoice) => void;
}) {
  const isArchive = mode === 'archive';
  // Archiving asks what becomes of the project's boards whenever there are any.
  const needsBoardsChoice = isArchive && boards.length > 0 && !!onBoardsChoiceChange;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{isArchive ? 'Archive Project' : 'Unarchive Project'}</AlertDialogTitle>
          <AlertDialogDescription>
            {isArchive ? (
              <>
                Are you sure you want to archive <strong>{project.name}</strong>? The project will be hidden from the default view but can be restored later.
              </>
            ) : (
              <>
                Restore <strong>{project.name}</strong> to active? It will reappear in the default project list.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {isArchive && (needsBoardsChoice || boardsLoadFailed) && (
          <ArchiveBoardsChoice
            boards={boards}
            value={boardsChoice}
            onChange={onBoardsChoiceChange!}
            loadFailed={boardsLoadFailed}
          />
        )}
        <AlertDialogFooter>
          <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            variant={isArchive ? 'destructive' : 'default'}
            className="cursor-pointer"
            // Not until the boards are in: an early click would archive without the question.
            disabled={
              isPending ||
              (isArchive && (isLoadingBoards || boardsLoadFailed)) ||
              (needsBoardsChoice && !boardsChoice)
            }
          >
            {isPending
              ? isArchive
                ? 'Archiving...'
                : 'Restoring...'
              : isArchive
                ? 'Archive'
                : 'Unarchive'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ProjectDetailSkeleton() {
  return (
    <>
      <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur px-6">
        <Skeleton className="h-4 w-48" />
      </header>
      <div className="p-6 max-w-6xl">
        <Skeleton className="h-4 w-32 mb-5" />
        <div className="flex items-start gap-4">
          <Skeleton className="h-14 w-14 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-64" />
            <Skeleton className="h-4 w-96" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
        <div className="mt-6 border-b border-border pb-3 flex gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-5 w-24" />
          ))}
        </div>
        <div className="mt-6 grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-lg" />
          ))}
        </div>
      </div>
    </>
  );
}
